import {
  collection,
  doc,
  getDocs,
  getDoc,
  addDoc,
  updateDoc,
  query,
  where,
  orderBy,
  limit,
  serverTimestamp,
  onSnapshot,
  increment,
  Unsubscribe,
  writeBatch,
} from 'firebase/firestore';
import { db } from '@/shared/config/firebase';
import {
  SavChat,
  SavMessage,
  SavChatSchema,
  SavMessageSchema,
  CreateSavChatRequest,
  CreateSavMessageRequest,
  ChatStatus,
  SenderType,
  ReaderType,
  ChatReason,
  ChatReasonLabels,
} from '@/models/savChat';

/**
 * Repository pour gérer les chats SAV
 */
export class SavChatRepository {
  private static readonly COLLECTION_NAME = 'sav_chats';
  private static readonly MESSAGES_SUBCOLLECTION = 'messages';

  /**
   * Créer un nouveau chat SAV avec un message initial
   */
  static async createChat(request: CreateSavChatRequest): Promise<SavChat> {
    try {
      console.log('🔄 Création d\'un nouveau chat SAV pour la commande:', request.orderId);

      const chatData = {
        orderId: request.orderId,
        organizationId: request.organizationId,
        customerId: request.customerId,
        customerName: request.customerName,
        customerEmail: request.customerEmail,
        orderReference: request.orderReference,
        status: ChatStatus.OPEN,
        reason: request.reason,
        reasonDetails: request.reasonDetails || '',
        lastMessageAt: serverTimestamp(),
        lastMessagePreview: request.initialMessage.substring(0, 100),
        unreadByAdmin: 1,
        unreadByCustomer: 0,
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp(),
      };

      const chatRef = await addDoc(collection(db, this.COLLECTION_NAME), chatData);

      // Ajouter le message initial
      const messageData = {
        senderType: SenderType.CUSTOMER,
        senderId: request.customerId,
        senderName: request.customerName,
        message: request.initialMessage,
        read: false,
        createdAt: serverTimestamp(),
      };

      await addDoc(
        collection(db, this.COLLECTION_NAME, chatRef.id, this.MESSAGES_SUBCOLLECTION),
        messageData
      );

      // Récupérer le chat créé avec les timestamps résolus
      const createdDoc = await getDoc(chatRef);
      const data = {
        id: chatRef.id,
        ...createdDoc.data(),
      };

      const chat = SavChatSchema.parse(data);
      console.log('✅ Chat SAV créé avec succès:', chat.id);
      return chat;
    } catch (error) {
      console.error('❌ Erreur lors de la création du chat SAV:', error);
      throw error;
    }
  }

  /**
   * Récupérer un chat existant par orderId et customerId (le plus récent)
   */
  static async getChatByOrderId(
    orderId: string,
    customerId: string
  ): Promise<SavChat | null> {
    try {
      const chats = await this.getChatsByOrderId(orderId, customerId);
      return chats.length > 0 ? chats[0] : null; // Le plus récent (car trié desc)
    } catch (error) {
      console.error('❌ Erreur lors de la recherche du chat SAV:', error);
      throw error;
    }
  }

  /**
   * Récupérer tous les chats d'une commande
   */
  static async getChatsByOrderId(
    orderId: string,
    customerId: string
  ): Promise<SavChat[]> {
    try {
      const q = query(
        collection(db, this.COLLECTION_NAME),
        where('orderId', '==', orderId),
        where('customerId', '==', customerId),
        orderBy('createdAt', 'desc')
      );

      const querySnapshot = await getDocs(q);

      const chats = querySnapshot.docs.map((doc) => {
        const data = {
          id: doc.id,
          ...doc.data(),
        };
        try {
          return SavChatSchema.parse(data);
        } catch (error) {
          console.error('❌ Erreur lors du parsing du chat:', error);
          return null;
        }
      }).filter((chat): chat is SavChat => chat !== null);

      return chats;
    } catch (error) {
      console.error('❌ Erreur lors de la récupération des chats de la commande:', error);
      throw error;
    }
  }

  /**
   * Récupérer un chat par son ID
   */
  static async getChatById(chatId: string): Promise<SavChat | null> {
    try {
      const chatRef = doc(db, this.COLLECTION_NAME, chatId);
      const chatDoc = await getDoc(chatRef);

      if (!chatDoc.exists()) {
        return null;
      }

      const data = {
        id: chatDoc.id,
        ...chatDoc.data(),
      };

      return SavChatSchema.parse(data);
    } catch (error) {
      console.error('❌ Erreur lors de la récupération du chat:', error);
      throw error;
    }
  }

  /**
   * Récupérer tous les chats d'une organisation
   */
  static async getChatsForOrganization(organizationId: string): Promise<SavChat[]> {
    try {
      console.log('🔍 Récupération des chats SAV pour l\'organisation:', organizationId);

      const q = query(
        collection(db, this.COLLECTION_NAME),
        where('organizationId', '==', organizationId),
        orderBy('lastMessageAt', 'desc')
      );

      const querySnapshot = await getDocs(q);

      const chats = querySnapshot.docs.map((doc) => {
        const data = {
          id: doc.id,
          ...doc.data(),
        };
        return SavChatSchema.parse(data);
      });

      console.log(`✅ ${chats.length} chats SAV récupérés`);
      return chats;
    } catch (error) {
      console.error('❌ Erreur lors de la récupération des chats SAV:', error);
      throw error;
    }
  }

  /**
   * Récupérer tous les chats d'un client
   */
  static async getChatsForCustomer(customerId: string): Promise<SavChat[]> {
    try {
      console.log('🔍 Récupération des chats SAV pour le client:', customerId);

      const q = query(
        collection(db, this.COLLECTION_NAME),
        where('customerId', '==', customerId)
      );

      const querySnapshot = await getDocs(q);

      const chats = querySnapshot.docs.map((doc) => {
        const data = {
          id: doc.id,
          ...doc.data(),
        };
        return SavChatSchema.parse(data);
      }).sort((a, b) => {
        const dateA = a.lastMessageAt ? a.lastMessageAt.getTime() : 0;
        const dateB = b.lastMessageAt ? b.lastMessageAt.getTime() : 0;
        return dateB - dateA;
      });

      console.log(`✅ ${chats.length} chats SAV récupérés pour le client`);
      return chats;
    } catch (error) {
      console.error('❌ Erreur lors de la récupération des chats du client:', error);
      throw error;
    }
  }

  /**
   * Mettre à jour le statut d'un chat
   */
  static async updateChatStatus(chatId: string, status: ChatStatus): Promise<void> {
    try {
      console.log(`🔄 Mise à jour du statut du chat ${chatId} vers ${status}`);

      const chatRef = doc(db, this.COLLECTION_NAME, chatId);
      await updateDoc(chatRef, {
        status,
        updatedAt: serverTimestamp(),
      });

      console.log('✅ Statut du chat mis à jour avec succès');
    } catch (error) {
      console.error('❌ Erreur lors de la mise à jour du statut:', error);
      throw error;
    }
  }

  /**
   * Réouvrir un chat avec un nouveau motif (ajoute un message système)
   */
  static async reopenChat(
    chatId: string,
    newReason: ChatReason,
    newReasonDetails: string
  ): Promise<void> {
    try {
      console.log(`🔄 Réouverture du chat ${chatId}`);

      const chatDocRef = doc(db, this.COLLECTION_NAME, chatId);
      const chatDoc = await getDoc(chatDocRef);

      if (!chatDoc.exists()) throw new Error('Chat introuvable');
      const chatData = chatDoc.data() as SavChat;



      const systemMessageText = `Discussion réouverte.\nAncien motif : ${ChatReasonLabels[chatData.reason] || chatData.reason}${chatData.reasonDetails ? ` (${chatData.reasonDetails})` : ''}\nNouveau motif : ${ChatReasonLabels[newReason] || newReason}${newReasonDetails ? ` (${newReasonDetails})` : ''}`;

      const batch = writeBatch(db);

      // 1. Ajouter le message système
      const messagesCollectionRef = collection(db, this.COLLECTION_NAME, chatId, this.MESSAGES_SUBCOLLECTION);
      const newMessageRef = doc(messagesCollectionRef);

      batch.set(newMessageRef, {
        senderType: SenderType.SYSTEM,
        senderId: 'system',
        senderName: 'Système',
        message: systemMessageText,
        read: true, // Toujours lu
        createdAt: serverTimestamp(),
      });

      // 2. Mettre à jour le chat
      batch.update(chatDocRef, {
        reason: newReason,
        reasonDetails: newReasonDetails,
        status: ChatStatus.OPEN,
        lastMessageAt: serverTimestamp(), // Pour le faire remonter
        updatedAt: serverTimestamp(),
      });

      await batch.commit();

      console.log('✅ Chat réouvert avec succès et historique préservé');
    } catch (error) {
      console.error('❌ Erreur lors de la réouverture du chat:', error);
      throw error;
    }
  }

  /**
   * Ajouter un message à un chat
   */
  static async addMessage(
    chatId: string,
    message: CreateSavMessageRequest
  ): Promise<void> {
    try {
      console.log(`🔄 Ajout d'un message au chat ${chatId}`);

      const messageData = {
        senderType: message.senderType,
        senderId: message.senderId,
        senderName: message.senderName,
        message: message.message,
        read: false,
        createdAt: serverTimestamp(),
      };

      // Ajouter le message
      await addDoc(
        collection(db, this.COLLECTION_NAME, chatId, this.MESSAGES_SUBCOLLECTION),
        messageData
      );

      // Mettre à jour le chat avec le dernier message
      const chatRef = doc(db, this.COLLECTION_NAME, chatId);
      const updateData: any = {
        lastMessageAt: serverTimestamp(),
        lastMessagePreview: message.message.substring(0, 100),
        updatedAt: serverTimestamp(),
      };

      // Incrémenter le compteur de messages non lus
      if (message.senderType === SenderType.CUSTOMER) {
        updateData.unreadByAdmin = increment(1);
      } else {
        updateData.unreadByCustomer = increment(1);
      }

      // Si le chat est fermé et qu'un nouveau message arrive, le rouvrir
      const chatDoc = await getDoc(chatRef);
      if (chatDoc.exists() && chatDoc.data().status === ChatStatus.CLOSED) {
        updateData.status = ChatStatus.OPEN;
      }

      // Si c'est un admin qui répond et que le chat est ouvert, passer en IN_PROGRESS
      if (
        message.senderType === SenderType.ADMIN &&
        chatDoc.exists() &&
        chatDoc.data().status === ChatStatus.OPEN
      ) {
        updateData.status = ChatStatus.IN_PROGRESS;
      }

      await updateDoc(chatRef, updateData);

      console.log('✅ Message ajouté avec succès');
    } catch (error) {
      console.error('❌ Erreur lors de l\'ajout du message:', error);
      throw error;
    }
  }

  /**
   * Marquer les messages comme lus
   * Note: On ne met à jour que le compteur sur le chat parent car les règles Firestore
   * interdisent la mise à jour des messages individuels (allow update: if false)
   */
  static async markMessagesAsRead(chatId: string, readerType: ReaderType): Promise<void> {
    try {
      console.log(`🔄 Marquage des messages comme lus pour le chat ${chatId}, readerType: ${readerType}`);

      const chatRef = doc(db, this.COLLECTION_NAME, chatId);

      // Vérifier d'abord si le chat existe et a des messages non lus
      const chatDoc = await getDoc(chatRef);
      if (!chatDoc.exists()) {
        console.log('❌ Chat introuvable');
        return;
      }

      const chatData = chatDoc.data();
      const unreadCount = readerType === 'admin' ? chatData.unreadByAdmin : chatData.unreadByCustomer;

      // Si pas de messages non lus, pas besoin de faire quoi que ce soit
      if (!unreadCount || unreadCount === 0) {
        console.log('ℹ️ Pas de messages non lus à marquer');
        return;
      }

      // Réinitialiser le compteur de messages non lus sur le chat
      // Note: On ne met pas à jour les messages individuels car les règles Firestore l'interdisent
      const updateData: Record<string, any> = {
        updatedAt: serverTimestamp(),
      };

      if (readerType === 'admin') {
        updateData.unreadByAdmin = 0;
      } else {
        updateData.unreadByCustomer = 0;
      }

      await updateDoc(chatRef, updateData);

      console.log(`✅ Compteur de messages non lus réinitialisé avec succès`);
    } catch (error) {
      console.error('❌ Erreur lors du marquage des messages comme lus:', error);
      throw error;
    }
  }

  /**
   * Écouter les changements d'un chat en temps réel
   */
  static subscribeToChat(chatId: string, callback: (chat: SavChat | null) => void): Unsubscribe {
    const chatRef = doc(db, this.COLLECTION_NAME, chatId);

    return onSnapshot(
      chatRef,
      (docSnapshot) => {
        if (!docSnapshot.exists()) {
          callback(null);
          return;
        }

        const data = {
          id: docSnapshot.id,
          ...docSnapshot.data(),
        };

        try {
          const chat = SavChatSchema.parse(data);
          callback(chat);
        } catch (error) {
          console.error('❌ Erreur lors du parsing du chat:', error);
          callback(null);
        }
      },
      (error) => {
        console.error('❌ Erreur dans la subscription du chat:', error);
        callback(null);
      }
    );
  }

  /**
   * Écouter les messages d'un chat en temps réel
   */
  static subscribeToMessages(
    chatId: string,
    callback: (messages: SavMessage[]) => void,
    messageLimit: number = 50
  ): Unsubscribe {
    const q = query(
      collection(db, this.COLLECTION_NAME, chatId, this.MESSAGES_SUBCOLLECTION),
      orderBy('createdAt', 'desc'),
      limit(messageLimit)
    );

    return onSnapshot(
      q,
      (querySnapshot) => {
        const messages = querySnapshot.docs
          .map((doc) => {
            const data = {
              id: doc.id,
              ...doc.data(),
            };
            try {
              return SavMessageSchema.parse(data);
            } catch (error) {
              console.error('❌ Erreur lors du parsing du message:', error);
              return null;
            }
          })
          .filter((msg): msg is SavMessage => msg !== null)
          .reverse(); // Inverser pour avoir les messages du plus ancien au plus récent

        callback(messages);
      },
      (error) => {
        console.error('❌ Erreur dans la subscription des messages:', error);
        callback([]);
      }
    );
  }

  /**
   * Écouter tous les chats d'une organisation en temps réel
   */
  static subscribeToOrganizationChats(
    organizationId: string,
    callback: (chats: SavChat[]) => void
  ): Unsubscribe {
    const q = query(
      collection(db, this.COLLECTION_NAME),
      where('organizationId', '==', organizationId),
      orderBy('lastMessageAt', 'desc')
    );

    return onSnapshot(
      q,
      (querySnapshot) => {
        const chats = querySnapshot.docs
          .map((doc) => {
            const data = {
              id: doc.id,
              ...doc.data(),
            };
            try {
              return SavChatSchema.parse(data);
            } catch (error) {
              console.error('❌ Erreur lors du parsing du chat:', error);
              return null;
            }
          })
          .filter((chat): chat is SavChat => chat !== null);

        callback(chats);
      },
      (error) => {
        console.error('❌ Erreur dans la subscription des chats:', error);
        callback([]);
      }
    );
  }

  /**
   * Écouter les chats d'un client en temps réel
   */
  static subscribeToCustomerChats(
    customerId: string,
    callback: (chats: SavChat[]) => void
  ): Unsubscribe {
    const q = query(
      collection(db, this.COLLECTION_NAME),
      where('customerId', '==', customerId)
      // orderBy retiré pour éviter erreur index manquant. Le tri est fait côté client.
    );

    return onSnapshot(
      q,
      (querySnapshot) => {
        const chats = querySnapshot.docs
          .map((doc) => {
            const data = {
              id: doc.id,
              ...doc.data(),
            };
            try {
              return SavChatSchema.parse(data);
            } catch (error) {
              console.error('❌ Erreur lors du parsing du chat:', error);
              return null;
            }
          })
          .filter((chat): chat is SavChat => chat !== null);

        callback(chats);
      },
      (error) => {
        console.error('❌ Erreur dans la subscription des chats du client:', error);
        callback([]);
      }
    );
  }

  /**
   * Écouter un chat spécifique par orderId en temps réel
   * Retourne le chat avec le plus de messages non lus, ou le plus récent s'il n'y a pas de messages non lus
   */
  static subscribeToOrderChat(
    orderId: string,
    customerId: string,
    callback: (chat: SavChat | null) => void,
    errorCallback?: (error: Error) => void
  ): Unsubscribe {
    const q = query(
      collection(db, this.COLLECTION_NAME),
      where('orderId', '==', orderId),
      where('customerId', '==', customerId)
      // Pas de limit pour récupérer tous les chats de cette commande
    );

    return onSnapshot(
      q,
      (querySnapshot) => {
        if (querySnapshot.empty) {
          callback(null);
          return;
        }

        // Parser tous les chats
        const chats: SavChat[] = [];
        querySnapshot.docs.forEach((chatDoc) => {
          const data = {
            id: chatDoc.id,
            ...chatDoc.data(),
          };
          try {
            const chat = SavChatSchema.parse(data);
            chats.push(chat);
          } catch (error) {
            console.error('❌ Erreur lors du parsing du chat:', error);
          }
        });

        if (chats.length === 0) {
          callback(null);
          return;
        }

        // Calculer le total des messages non lus pour tous les chats
        const totalUnread = chats.reduce((sum, chat) => sum + (chat.unreadByCustomer || 0), 0);

        // Trouver le chat le plus pertinent (celui avec des messages non lus, ou le plus récent)
        const chatWithUnread = chats.find(c => c.unreadByCustomer > 0);
        const mostRecentChat = chats.sort((a, b) => {
          const dateA = a.lastMessageAt ? new Date(a.lastMessageAt).getTime() : 0;
          const dateB = b.lastMessageAt ? new Date(b.lastMessageAt).getTime() : 0;
          return dateB - dateA;
        })[0];

        const selectedChat = chatWithUnread || mostRecentChat;

        // Retourner le chat avec le total des messages non lus de tous les chats
        callback({
          ...selectedChat,
          unreadByCustomer: totalUnread
        });
      },
      (error) => {
        console.error('❌ Erreur dans la subscription du chat:', error);
        if (errorCallback) errorCallback(error as Error);
        callback(null);
      }
    );
  }
}

